import { FilterPreset, PolicyFilterIntersection } from '@warebee/shared/common';
import {
  ActualityHqDataColumn,
  ActualityHqWithCongestionsRow,
} from '@warebee/shared/dataset-queries';
import { TFunction } from 'i18next';
import _ from 'lodash';
import { formatDateTime, formatTimespan } from '../../common/formatHelper';
import { itemSetMetrics } from '../../metrics/itemSet/itemSetMetric.types';
import { locationMetrics } from '../../metrics/location/locationMetric.types';

export type EventIssueTiming = 'before' | 'during' | 'after';

export interface EventIssueConfig {
  key: string;
  title: (t: TFunction) => string;
  getValue: (
    event: ActualityHqWithCongestionsRow,
    t: TFunction,
  ) => string | null;
  getUnitOfMeasure?: (t: TFunction) => string;
  timing: EventIssueTiming;
  condition: (event: ActualityHqWithCongestionsRow) => boolean;
  severity?: 'info' | 'warning' | 'error';
  filterConfig?: PolicyFilterIntersection<ActualityHqDataColumn>;
}

export interface EventStatConfig {
  key: string;
  title: (t: TFunction) => string;
  getValue: (
    event: ActualityHqWithCongestionsRow,
    t: TFunction,
  ) => string | number | null;
  getUnitOfMeasure?: (t: TFunction) => string;
  category: EventStatCategory;
  groupings: EventGrouping[];
  defaultVisible: boolean;
  canCopy?: boolean;
  truncate?: boolean;
  format?: 'datetime' | 'timespan' | 'number' | 'string';
}

export type EventStatCategory =
  | 'identity'
  | 'timing'
  | 'location'
  | 'process'
  | 'agent'
  | 'item'
  | 'performance'
  | 'quality'
  | 'technical';

export type EventGrouping =
  | 'agentId'
  | 'agent'
  | 'location'
  | 'item'
  | 'process'
  | 'time'
  | 'all';

export interface EventStatCategoryConfig {
  key: EventStatCategory;
  title: (t: TFunction) => string;
  icon?: string;
  defaultExpanded: boolean;
  groupPriority: Record<EventGrouping, number>; // 0 = highest priority
}

export interface EventGroupingConfig {
  key: EventGrouping;
  title: (t: TFunction) => string;
  description: (t: TFunction) => string;
  primaryStats: string[]; // stat keys to show prominently
  secondaryStats: string[]; // stat keys to show in expanded view
  defaultIssues: string[]; // issue keys to show by default
  icon: string; // Icon component name
  keyFields: string[]; // Fields that identify this grouping type
}

// Event Issues Configuration
export const EVENT_ISSUES_CONFIG: EventIssueConfig[] = [
  {
    key: 'idleTime',
    title: t => t('Idle Time', { ns: 'feed' }),
    getValue: event =>
      event.idleDurationBetweenJobs > 0
        ? formatTimespan(event.idleDurationBetweenJobs)
        : null,
    getUnitOfMeasure: t => t('s', { ns: 'feed' }),
    timing: 'before',
    condition: event => event.idleDurationBetweenJobs > 0,
    severity: 'warning',
    filterConfig: {
      id: 'issuedIdleTime',
      allOf: [
        {
          type: 'idleDurationBetweenJobs',
          range: { from: 600000, to: 3600000, excludeFrom: true },
        },
      ],
    },
  },
  {
    key: 'congestedTime',
    title: t => t('Congested Time', { ns: 'feed' }),
    getValue: event =>
      event.congestedEventTime?.length > 0
        ? `${event.congestedEventTime.length} congested events`
        : null,
    timing: 'before',
    condition: event => event.congestedEventTime?.length > 0,
    severity: 'warning',
  },
  {
    key: 'locationAssignmentIssue',
    title: t => t('Location Assignment Issue', { ns: 'feed' }),
    getValue: () => 'Yes',
    timing: 'before',
    condition: event => event.hasIssueWithLocationPerProduct === 1,
    severity: 'error',
  },
  {
    key: 'outOfSequencePick',
    title: t => t('Out of Sequence pick', { ns: 'feed' }),
    getValue: (event, t) =>
      t('{{locationId}}', {
        locationId: event.layoutLocationId,
      }),
    timing: 'before',
    condition: event =>
      event.hasIssueWithProductSequence === 1 &&
      !_.isNil(event.layoutLocationId),
    severity: 'error',
  },
  {
    key: 'locationNotFound',
    title: t => t('Location Not Found', { ns: 'feed' }),
    getValue: (event, t) =>
      t('{{locationId}}', {
        locationId: event.layoutLocationId,
      }),
    timing: 'before',
    condition: event => _.isNil(event.layoutLocationId),
    severity: 'error',
  },
  {
    key: 'workStartDelay',
    title: t => t('Work Start Delay', { ns: 'feed' }),
    getValue: event =>
      event.idleDurationShiftStart > 0
        ? formatTimespan(event.idleDurationShiftStart)
        : null,
    timing: 'before',
    condition: event => event.idleDurationShiftStart > 0,
    severity: 'info',
  },
  {
    key: 'workEndDelay',
    title: t => t('Work End Delay', { ns: 'feed' }),
    getValue: event =>
      event.idleDurationShiftEnd > 0
        ? formatTimespan(event.idleDurationShiftEnd)
        : null,
    timing: 'before',
    condition: event => event.idleDurationShiftEnd > 0,
    severity: 'info',
  },
  {
    key: 'analyseIssue',
    title: t => t('Analyse Issue', { ns: 'feed' }),
    getValue: event =>
      event.reasons?.length > 0 ? event.reasons.join(', ') : null,
    timing: 'during',
    condition: event => event.reasons?.length > 0,
    severity: 'error',
  },
  {
    key: 'dataTimeSync',
    title: t => t('Data Time Sync', { ns: 'feed' }),
    getValue: event => {
      if (!event.eventStartTime) return null;
      const offset = Math.abs(
        event.eventEndTime.getTime() - event.eventStartTime.getTime(),
      );
      const delayDuration = 30000; // 30 seconds threshold
      return offset > delayDuration ? formatTimespan(offset) : null;
    },
    timing: 'after',
    condition: event => {
      if (!event.eventStartTime) return false;
      const offset = Math.abs(
        event.eventEndTime.getTime() - event.eventStartTime.getTime(),
      );
      const delayDuration = 30000; // 30 seconds threshold
      return offset > delayDuration;
    },
    severity: 'warning',
  },
];

export const eventIssueFilterPreset: FilterPreset<ActualityHqDataColumn> = {
  id: '',
  title: '',
  filterUnion: {
    anyOf: _(EVENT_ISSUES_CONFIG)
      .map(cfg => cfg.filterConfig)
      .compact()
      .value(),
  },
};

// Event Stats Configuration
export const EVENT_STATS_CONFIG: EventStatConfig[] = [
  // Identity
  {
    key: 'eventId',
    title: t => t('ID', { ns: 'feed' }),
    getValue: event => event.eventId,
    category: 'identity',
    groupings: ['all'],
    defaultVisible: false,
    canCopy: true,
    truncate: true,
  },
  {
    key: 'jobId',
    title: t => t('Job ID', { ns: 'feed' }),
    getValue: event => event.jobId,
    category: 'identity',
    groupings: ['agent', 'process', 'all'],
    defaultVisible: true,
    canCopy: true,
    truncate: true,
  },
  {
    key: 'jobGroupId',
    title: t => t('Order ID', { ns: 'feed' }),
    getValue: event => event.jobGroupId,
    category: 'identity',
    groupings: ['agent', 'process', 'all'],
    defaultVisible: true,
    canCopy: true,
    truncate: true,
  },

  // Timing
  {
    key: 'eventStartTime',
    title: t => t('Start Time', { ns: 'feed' }),
    getValue: event =>
      event.eventStartTime ? formatDateTime(event.eventStartTime) : null,
    category: 'timing',
    groupings: ['time', 'all', 'process'],
    defaultVisible: true,
    format: 'datetime',
  },
  {
    key: 'eventEndTime',
    title: t => t('End Time', { ns: 'feed' }),
    getValue: event => formatDateTime(event.eventEndTime),
    category: 'timing',
    groupings: ['time', 'all', 'process'],
    defaultVisible: true,
    format: 'datetime',
  },
  {
    key: 'duration',
    title: t => t('Duration', { ns: 'feed' }),
    getValue: event => event.duration,
    getUnitOfMeasure: t => t('s', { ns: 'feed' }),
    category: 'timing',
    groupings: ['agent', 'process', 'time', 'all'],
    defaultVisible: true,
    format: 'timespan',
  },

  // Location
  {
    key: 'locationId',
    title: t => t('Location', { ns: 'feed' }),
    getValue: event => event.locationId,
    category: 'location',
    groupings: ['location', 'agent', 'all'],
    defaultVisible: true,
  },
  {
    key: 'locationIdSource',
    title: t => t('Source Location', { ns: 'feed' }),
    getValue: event => event.locationIdSource,
    category: 'location',
    groupings: ['location', 'agent', 'all'],
    defaultVisible: true,
  },
  {
    key: 'locationBayTitle',
    title: t => t('Bay', { ns: 'feed' }),
    getValue: event => event.locationBayTitle,
    category: 'location',
    groupings: ['location', 'all'],
    defaultVisible: false,
  },
  {
    key: 'layoutWarehouseArea',
    title: t => t('Area', { ns: 'feed' }),
    getValue: event => event.layoutWarehouseArea,
    category: 'location',
    groupings: ['location', 'all'],
    defaultVisible: false,
  },

  // Process
  {
    key: 'eventProcessType',
    title: t => t('Process', { ns: 'feed' }),
    getValue: event => event.eventProcessType,
    category: 'process',
    groupings: ['process', 'agent', 'all'],
    defaultVisible: true,
  },
  {
    key: 'eventType',
    title: t => t('Event Type', { ns: 'feed' }),
    getValue: event => event.eventType,
    category: 'process',
    groupings: ['process', 'all'],
    defaultVisible: false,
  },
  {
    key: 'jobLineSequence',
    title: t => t('Line Sequence', { ns: 'feed' }),
    getValue: event => event.jobLineSequence,
    category: 'process',
    groupings: ['process', 'agent', 'all'],
    defaultVisible: false,
  },

  // Agent
  {
    key: 'agentId',
    title: t => t('Agent ID', { ns: 'feed' }),
    getValue: event => event.agentId,
    category: 'agent',
    groupings: ['agentId', 'all'],
    defaultVisible: true,
  },
  {
    key: 'agentType',
    title: t => t('Agent Type', { ns: 'feed' }),
    getValue: event => event.agentType,
    category: 'agent',
    groupings: ['agent', 'all'],
    defaultVisible: true,
  },
  {
    key: 'agentEnergy',
    title: t => t('Device Energy', { ns: 'feed' }),
    getValue: event => event.agentEnergy,
    getUnitOfMeasure: t => t('%', { ns: 'feed' }),
    category: 'agent',
    groupings: ['agent', 'agentId', 'all'],
    defaultVisible: false,
  },
  {
    key: 'agentSignalStrength',
    title: t => t('Signal Strength', { ns: 'feed' }),
    getValue: event => event.agentSignalStrength,
    getUnitOfMeasure: t => t('dBm', { ns: 'feed' }),
    category: 'agent',
    groupings: ['agent', 'agentId', 'all'],
    defaultVisible: false,
  },

  // Item
  {
    key: 'sku',
    title: t => t('Item (SKU)', { ns: 'feed' }),
    getValue: event => event.sku,
    category: 'item',
    groupings: ['item', 'all'],
    defaultVisible: true,
  },
  {
    key: 'consignee',
    title: t => t('Consignee', { ns: 'feed' }),
    getValue: event => event.consignee,
    category: 'item',
    groupings: ['item', 'all'],
    defaultVisible: false,
  },
  {
    key: 'uom',
    title: t => t('UOM', { ns: 'feed' }),
    getValue: event => event.uom,
    category: 'item',
    groupings: ['item', 'all'],
    defaultVisible: true,
  },
  {
    key: 'quantity',
    title: t => t('Qty', { ns: 'feed' }),
    getValue: event => event.quantity,
    category: 'item',
    groupings: ['item', 'all'],
    defaultVisible: true,
  },
  {
    key: 'skuGroup',
    title: t => t('SKU Group', { ns: 'feed' }),
    getValue: event => event.skuGroup,
    category: 'item',
    groupings: ['item', 'all'],
    defaultVisible: false,
  },

  // Performance
  {
    key: 'distance',
    title: t => t('Distance', { ns: 'feed' }),
    getValue: event => event.distance,
    getUnitOfMeasure: t => t('cm', { ns: 'feed' }),
    category: 'performance',
    groupings: ['agent', 'process', 'all'],
    defaultVisible: false,
  },
  {
    key: 'cost',
    title: t => t('Cost', { ns: 'feed' }),
    getValue: event => event.cost,
    category: 'performance',
    groupings: ['agent', 'process', 'all'],
    defaultVisible: false,
  },
  {
    key: 'durationTravellingInternal',
    title: t => t('Travel Time', { ns: 'feed' }),
    getValue: event => event.durationTravellingInternal,
    getUnitOfMeasure: t => t('s', { ns: 'feed' }),
    category: 'performance',
    groupings: ['agent', 'process', 'all'],
    defaultVisible: false,
    format: 'timespan',
  },
  {
    key: 'durationHandling',
    title: t => t('Handling Time', { ns: 'feed' }),
    getValue: event => event.durationHandling,
    getUnitOfMeasure: t => t('s', { ns: 'feed' }),
    category: 'performance',
    groupings: ['agent', 'process', 'all'],
    defaultVisible: false,
    format: 'timespan',
  },
  {
    key: 'durationPicking',
    title: t => t('Picking Time', { ns: 'feed' }),
    getValue: event => event.durationPicking,
    getUnitOfMeasure: t => t('s', { ns: 'feed' }),
    category: 'performance',
    groupings: ['agent', 'process', 'all'],
    defaultVisible: false,
    format: 'timespan',
  },

  // Quality
  {
    key: 'congestedEvents',
    title: t => t('Congested Events', { ns: 'feed' }),
    getValue: event => event.congestedEvents?.length || 0,
    category: 'quality',
    groupings: ['agent', 'location', 'all'],
    defaultVisible: false,
  },
  {
    key: 'hasIssueWithProductSequence',
    title: t => t('Sequence Issue', { ns: 'feed' }),
    getValue: event => (event.hasIssueWithProductSequence === 1 ? 'Yes' : 'No'),
    category: 'quality',
    groupings: ['agent', 'process', 'all'],
    defaultVisible: false,
  },
  {
    key: 'hasIssueWithLocationPerProduct',
    title: t => t('Location Assignment Issue', { ns: 'feed' }),
    getValue: event =>
      event.hasIssueWithLocationPerProduct === 1 ? 'Yes' : 'No',
    category: 'quality',
    groupings: ['agent', 'location', 'all'],
    defaultVisible: false,
  },
];

// Category Configuration
export const EVENT_STAT_CATEGORIES_CONFIG: Record<
  EventStatCategory,
  EventStatCategoryConfig
> = {
  identity: {
    key: 'identity',
    title: t => t('Identity', { ns: 'feed' }),
    icon: 'IdCard',
    defaultExpanded: false,
    groupPriority: {
      agentId: 3,
      agent: 3,
      location: 3,
      item: 3,
      process: 3,
      time: 3,
      all: 3,
    },
  },
  timing: {
    key: 'timing',
    title: t => t('Timing', { ns: 'feed' }),
    icon: 'Clock',
    defaultExpanded: true,
    groupPriority: {
      agentId: 1,
      agent: 1,
      location: 2,
      item: 2,
      process: 1,
      time: 0,
      all: 1,
    },
  },
  location: {
    key: 'location',
    title: t => t('Location', { ns: 'feed' }),
    icon: 'MapPin',
    defaultExpanded: true,
    groupPriority: {
      agentId: 2,
      agent: 2,
      location: 0,
      item: 1,
      process: 2,
      time: 2,
      all: 2,
    },
  },
  process: {
    key: 'process',
    title: t => t('Process', { ns: 'feed' }),
    icon: 'Settings',
    defaultExpanded: true,
    groupPriority: {
      agentId: 1,
      agent: 1,
      location: 1,
      item: 2,
      process: 0,
      time: 1,
      all: 1,
    },
  },
  agent: {
    key: 'agent',
    title: t => t('Agent', { ns: 'feed' }),
    icon: 'User',
    defaultExpanded: true,
    groupPriority: {
      agentId: 0,
      agent: 0,
      location: 3,
      item: 3,
      process: 1,
      time: 3,
      all: 2,
    },
  },
  item: {
    key: 'item',
    title: t => t('Item', { ns: 'feed' }),
    icon: 'Package',
    defaultExpanded: true,
    groupPriority: {
      agentId: 3,
      agent: 3,
      location: 1,
      item: 0,
      process: 2,
      time: 2,
      all: 1,
    },
  },
  performance: {
    key: 'performance',
    title: t => t('Performance', { ns: 'feed' }),
    icon: 'TrendingUp',
    defaultExpanded: false,
    groupPriority: {
      agentId: 2,
      agent: 2,
      location: 2,
      item: 3,
      process: 1,
      time: 1,
      all: 3,
    },
  },
  quality: {
    key: 'quality',
    title: t => t('Quality', { ns: 'feed' }),
    icon: 'Shield',
    defaultExpanded: false,
    groupPriority: {
      agentId: 2,
      agent: 2,
      location: 2,
      item: 3,
      process: 2,
      time: 3,
      all: 3,
    },
  },
  technical: {
    key: 'technical',
    title: t => t('Technical', { ns: 'feed' }),
    icon: 'Monitor',
    defaultExpanded: false,
    groupPriority: {
      agentId: 3,
      agent: 3,
      location: 3,
      item: 3,
      process: 3,
      time: 3,
      all: 3,
    },
  },
};

// Grouping Configuration
export const EVENT_GROUPING_CONFIG: Record<EventGrouping, EventGroupingConfig> =
  {
    agentId: {
      key: 'agentId',
      title: t => t('Agent ID View', { ns: 'feed' }),
      description: t => t('Detailed view for specific agent', { ns: 'feed' }),
      primaryStats: [
        'agentId',
        'eventProcessType',
        'locationId',
        'sku',
        'duration',
      ],
      secondaryStats: [
        'agentEnergy',
        'agentSignalStrength',
        'jobId',
        'eventStartTime',
        'eventEndTime',
      ],
      defaultIssues: [
        'idleTime',
        'outOfSequencePick',
        'analyseIssue',
        'locationNotFound',
      ],
      icon: 'AgentStandingIso', // Changed from 'User' to available agent icon
      keyFields: ['agentId', 'agentUser'], // Added agentUser since it also identifies specific agents
    },
    agent: {
      key: 'agent',
      title: t => t('Agent Type View', { ns: 'feed' }),
      description: t => t('View grouped by agent type', { ns: 'feed' }),
      primaryStats: ['agentType', 'eventProcessType', 'duration', 'distance'],
      secondaryStats: [
        'agentId',
        'locationId',
        'cost',
        'durationTravellingInternal',
      ],
      defaultIssues: [
        'idleTime',
        'congestedTime',
        'workStartDelay',
        'locationNotFound',
      ],
      icon: 'AgentStandingIso', // Changed from 'User' to available agent icon
      keyFields: ['agentType', 'agentName'], // Specific to agent type/name groupings
    },
    location: {
      key: 'location',
      title: t => t('Location View', { ns: 'feed' }),
      description: t => t('View grouped by location', { ns: 'feed' }),
      primaryStats: ['locationId', 'layoutWarehouseArea', 'duration'],
      secondaryStats: [
        'locationBayTitle',
        'agentType',
        'sku',
        'congestedEvents',
      ],
      defaultIssues: ['locationAssignmentIssue', 'congestedTime'],
      icon: 'Bay',
      keyFields: [...locationMetrics],
    },
    item: {
      key: 'item',
      title: t => t('Item View', { ns: 'feed' }),
      description: t => t('View grouped by item/SKU', { ns: 'feed' }),
      primaryStats: ['sku', 'consignee', 'quantity', 'uom'],
      secondaryStats: ['skuGroup', 'locationId', 'duration', 'agentType'],
      defaultIssues: ['outOfSequencePick', 'analyseIssue'],
      icon: 'Pallet',
      keyFields: [...itemSetMetrics, 'categoryAbc'],
    },
    process: {
      key: 'process',
      title: t => t('Process View', { ns: 'feed' }),
      description: t => t('View grouped by process type', { ns: 'feed' }),
      primaryStats: ['eventProcessType', 'eventType', 'duration', 'distance'],
      secondaryStats: [
        'jobLineSequence',
        'agentType',
        'cost',
        'durationPicking',
      ],
      defaultIssues: ['analyseIssue', 'outOfSequencePick'],
      icon: 'PickGesture',
      keyFields: ['eventProcessType', 'eventType', 'jobId', 'jobGroupId'],
    },
    time: {
      key: 'time',
      title: t => t('Time View', { ns: 'feed' }),
      description: t => t('View grouped by time', { ns: 'feed' }),
      primaryStats: ['eventStartTime', 'eventEndTime', 'duration'],
      secondaryStats: ['agentId', 'eventProcessType', 'locationId'],
      defaultIssues: ['dataTimeSync', 'workStartDelay', 'workEndDelay'],
      icon: 'TimeFull',
      keyFields: ['eventHour', 'eventDay', 'eventWeek', 'eventEndTime'],
    },
    all: {
      key: 'all',
      title: t => t('Complete View', { ns: 'feed' }),
      description: t => t('All available information', { ns: 'feed' }),
      primaryStats: [
        'eventId',
        'agentId',
        'locationId',
        'sku',
        'eventProcessType',
        'duration',
      ],
      secondaryStats: [
        'jobId',
        'jobGroupId',
        'eventStartTime',
        'eventEndTime',
        'agentType',
      ],
      defaultIssues: [
        'idleTime',
        'congestedTime',
        'outOfSequencePick',
        'locationNotFound',
        'analyseIssue',
        'dataTimeSync',
      ],
      icon: 'List',
      keyFields: [],
    },
  };

// Helper functions
export const getStatsForGrouping = (
  grouping: EventGrouping,
): EventStatConfig[] => {
  return EVENT_STATS_CONFIG.filter(stat => stat.groupings.includes(grouping));
};

export const getIssuesForGrouping = (
  grouping: EventGrouping,
): EventIssueConfig[] => {
  const groupConfig = EVENT_GROUPING_CONFIG[grouping];
  return EVENT_ISSUES_CONFIG.filter(issue =>
    groupConfig.defaultIssues.includes(issue.key),
  );
};

export const getStatsByCategory = (
  category: EventStatCategory,
  grouping?: EventGrouping,
): EventStatConfig[] => {
  let stats = EVENT_STATS_CONFIG.filter(stat => stat.category === category);
  if (grouping) {
    stats = stats.filter(stat => stat.groupings.includes(grouping));
  }
  return stats;
};

export const getCategoriesForGrouping = (
  grouping: EventGrouping,
): EventStatCategoryConfig[] => {
  const categories = Object.values(EVENT_STAT_CATEGORIES_CONFIG);
  return categories.sort(
    (a, b) => a.groupPriority[grouping] - b.groupPriority[grouping],
  );
};

export const getIssuesByTiming = (
  timing: EventIssueTiming,
  grouping?: EventGrouping,
): EventIssueConfig[] => {
  let issues = EVENT_ISSUES_CONFIG.filter(issue => issue.timing === timing);
  if (grouping) {
    const groupConfig = EVENT_GROUPING_CONFIG[grouping];
    issues = issues.filter(issue =>
      groupConfig.defaultIssues.includes(issue.key),
    );
  }
  return issues;
};

/**
 * Global function to detect grouping from keyFields using configuration
 */
export const detectGroupingFromKeyFields = (
  keyFields: string[],
): EventGrouping => {
  // Check each grouping configuration to see if keyFields match
  for (const [groupingKey, config] of Object.entries(EVENT_GROUPING_CONFIG)) {
    if (config.keyFields.some(field => keyFields.includes(field))) {
      return groupingKey as EventGrouping;
    }
  }

  // Default fallback
  return 'all';
};

/**
 * Get icon for a specific grouping
 */
export const getGroupingIcon = (grouping: EventGrouping): string => {
  return EVENT_GROUPING_CONFIG[grouping].icon;
};
